import React from "react";
import { useUiLanguage } from "../ui/uiText";
import { sharedSettingsListDe, sharedSettingsListEn } from "./settingsListTranslations";
import type { LocalizedText, SettingsListPreferences, SettingsListRuntime } from "./settingsListTypes";
import { LANGUAGE_DE } from "../i18n/languageCodes";

const SettingsListRuntimeContext = React.createContext<SettingsListRuntime | null>(null);

/**
 * Laufzeit aller Shared-Listen eines Produkts (Konto, Texte, Präferenzen).
 * Seitenköpfe gehören dem Seitenrahmen, Abschnittstitel ausdrücklich der jeweiligen Liste.
 */
export function SettingsListRuntimeProvider({ value, children }: { value: SettingsListRuntime; children: React.ReactNode }) {
  return <SettingsListRuntimeContext.Provider value={value}>{children}</SettingsListRuntimeContext.Provider>;
}

const browserOnlyPreferences: SettingsListPreferences = { prefs: {}, isReady: false, patchPrefs: () => undefined };

/**
 * Laufzeit für Produkte ohne Serverpräferenzen: Sortierung und Spalten nur im Browser (je Konto, sofern
 * bekannt), Texte aus den Shared-Listentexten, sonst über `tr`.
 */
export function useBrowserSettingsListRuntime(
  accountId: string | null,
  tr?: (key: string) => string,
): SettingsListRuntime {
  const lang = useUiLanguage();
  return React.useMemo<SettingsListRuntime>(() => ({
    accountId,
    sortPreferences: browserOnlyPreferences,
    text: (value: LocalizedText) => {
      if (typeof value !== "string") return lang === LANGUAGE_DE ? value.de : value.en;
      return (lang === LANGUAGE_DE ? sharedSettingsListDe : sharedSettingsListEn)[value] ?? (tr ? tr(value) : value);
    },
  }), [accountId, lang, tr]);
}

/**
 * Laufzeit einer Liste: ausdrücklich übergeben, sonst aus dem Provider, sonst ohne Konto und ohne
 * Serverpräferenzen (Browserspeicher, Shared-Texte, Kartenkopf).
 */
export function useResolvedSettingsListRuntime(explicit?: SettingsListRuntime): SettingsListRuntime {
  const provided = React.useContext(SettingsListRuntimeContext);
  const fallback = useBrowserSettingsListRuntime(null);
  return explicit ?? provided ?? fallback;
}
